import { CubeIcon, CursorArrowIcon, GroupIcon } from '@radix-ui/react-icons'
import * as Toggle from '@radix-ui/react-toggle-group'
import PolygonSelectIcon from 'assets/polygon-select.svg?react'
import { useGeckoAnalytics } from 'features/analytics'
import { useKestrel, useKestrelViewerContext } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import { CreateCameraViewButton } from 'features/kestrelViewer/ui/tools/CreateCameraViewButton'
import { ToggleInspectionGridButton } from 'features/kestrelViewer/ui/tools/inspectionGrid/ToggleInspectionGridButton'
import { KestrelViewerTool } from 'features/kestrelViewer/ui/tools/KestrelViewerTool'
import { MeasurementModes } from 'features/kestrelViewer/ui/tools/MeasurementModes'
import { SegmentationModes } from 'features/kestrelViewer/ui/tools/SegmentationModes'
import { PermissionsGate } from 'features/permissions'
import React from 'react'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

export const PrimaryModes: React.FC = () => {
  const {
    sendCommand,
    selectedMode,
    setSelectedMode,
    setTextSegmentationPopoverVisible,
  } = useKestrelViewerContext()
  const { trackButtonClicked } = useGeckoAnalytics()
  const { kestrelModel, gatekeeper } = useKestrel()

  const handleModeChange = (value: string) => {
    if (value === '') return
    setSelectedMode(value)
    // Hide text segmentation popover when any other mode is selected
    setTextSegmentationPopoverVisible(false)

    if (value === 'temperature') {
      sendCommand({ command: KestrelCommands.SET_MODE, mode: 'cursor' })
      sendCommand({
        command: 'setPointerMode',
        createAnnotationOnClick: false,
      })
    } else {
      sendCommand({ command: KestrelCommands.SET_MODE, mode: value })
      if (value === 'cursor' || value === 'segmentation') {
        sendCommand({
          command: KestrelCommands.SET_POINTER_MODE,
          createAnnotationOnClick: true,
        })
      }
      if (value === 'inspectionArea') {
        sendCommand({
          command: KestrelCommands.SET_INSPECTION_AREA_DRAWING,
          enabled: true,
        })
      }
    }

    trackButtonClicked({
      button_name: `kestrel_set_mode`,
      kestrel_model_id: kestrelModel?.data.kestrel_model_id,
      mode: value,
    })
  }

  return (
    <Toggle.Root
      type='single'
      value={selectedMode}
      aria-label='Change mode'
      onClick={(event) => event.stopPropagation()}
      onValueChange={handleModeChange}>
      <KestrelViewerTool
        tooltipLabel={'Point Annotate'}
        modeId={'cursor'}
        selectedMode={selectedMode}>
        <CursorArrowIcon width={20} height={20} />
      </KestrelViewerTool>
      {gatekeeper.feature.drawInspectionAreas && (
        <KestrelViewerTool
          tooltipLabel={'Draw Inspection Areas'}
          modeId={'inspectionArea'}
          selectedMode={selectedMode}>
          <PolygonSelectIcon width={25} height={25} />
        </KestrelViewerTool>
      )}
      <KestrelViewerTool
        tooltipLabel={'Polygon Annotate / Edit'}
        modeId={'surfaceArea'}
        selectedMode={selectedMode}>
        <GroupIcon width={25} height={25} />
      </KestrelViewerTool>
      <SegmentationModes />
      <MeasurementModes />
      <CreateCameraViewButton />
      {gatekeeper.feature.toggleInspectionGridButton && (
        <ToggleInspectionGridButton />
      )}
      <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
        <KestrelViewerTool
          tooltipLabel={'Primitive Builder'}
          modeId={'primitivebuilder'}
          selectedMode={selectedMode}>
          <CubeIcon width={25} height={25} />
        </KestrelViewerTool>
      </PermissionsGate>
    </Toggle.Root>
  )
}
